// LT-134 — Contacts list page. Filters (lifecycle_stage, owner, account,
// time range), search (name/email/phone via backend ILIKE), CSV bulk
// upload button, row-click navigates to the detail page.
// LT-310: DS v1 migration — tokens, <Button>, <IconButton>, <Input>, <Select>.
// LT-313: DataTable adoption. Replaces the ad-hoc <table> with the
// shared <DataTable> primitive; adds bulk-select + bulk-email / bulk-SMS.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Search, Plus, Phone, Mail, MessageSquare, Megaphone, Users, X, Trash2, ListPlus } from 'lucide-react';

import PageLoader from '../../components/PageLoader';
import PageShell from '../../components/PageShell';
import SendEmailModal, { type EmailLeadTarget } from '../../components/SendEmailModal';
import SendSMSModal, { type SmsLeadTarget } from '../../components/SendSMSModal';
import BulkEmailModal from '../../components/BulkEmailModal';
import BulkSMSModal from '../../components/BulkSMSModal';
import AddToCampaignModal from '../../components/AddToCampaignModal';
import AddToListModal from '../../components/AddToListModal';
import LifecyclePill from '../../components/LifecyclePill';
import CallLeadModal, { type CallTarget } from '../LeadsPage/CallLeadModal';
import { useAuth } from '../../context/AuthContext';
import { useSmsEligibility } from '../../utils/entitlements';
import { accountsAPI } from '../../services/accountsAPI';
import { contactsAPI } from '../../services/contactsAPI';
import type { Account, Contact, ContactCreate, LifecycleStage } from '../../types/contact';
import { LIFECYCLE_STAGES, LIFECYCLE_STAGE_LABELS } from '../../types/contact';
import ContactsUploadModal, { type ContactsUploadResult } from './ContactsUploadModal';
import { Button, DataTable, EmptyState, IconButton, Input, Select, type ColumnDef } from '../../ui';


function displayName(c: Contact): string {
  const first = (c.first_name ?? '').trim();
  const last = (c.last_name ?? '').trim();
  const full = [first, last].filter(Boolean).join(' ');
  return full || (c.email ?? '') || (c.phone ?? '') || 'Unnamed contact';
}

// LT-328: filter dropdown surfaces all 9 lifecycle stages (Pass 1
// vocabulary + the 4 new Phase-3 stages). Built from `LIFECYCLE_STAGES`
// + `LIFECYCLE_STAGE_LABELS` so adding stages later is a one-place edit
// in `types/contact.ts`.
const LIFECYCLE_FILTERS: { value: '' | LifecycleStage; label: string }[] = [
  { value: '', label: 'All stages' },
  ...LIFECYCLE_STAGES.map((s) => ({ value: s, label: LIFECYCLE_STAGE_LABELS[s] })),
];

const TIME_RANGES: { value: '' | 'week' | 'month' | 'quarter'; label: string }[] = [
  { value: '', label: 'Any time' },
  { value: 'week', label: 'Last 7 days' },
  { value: 'month', label: 'Last 30 days' },
  { value: 'quarter', label: 'Last 90 days' },
];

function sinceFor(range: 'week' | 'month' | 'quarter'): string {
  const days = range === 'week' ? 7 : range === 'month' ? 30 : 90;
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

const ContactsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canSms } = useSmsEligibility(user);
  const canEmail = canSms;

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // Client-side pagination — backend returns up to 500 rows in one shot, we
  // page through them locally so the table doesn't become an infinite scroll.
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);

  const [lifecycle, setLifecycle] = useState<'' | LifecycleStage>('');
  const [accountId, setAccountId] = useState<string>('');
  const [timeRange, setTimeRange] = useState<'' | 'week' | 'month' | 'quarter'>('');
  const [query, setQuery] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadMsg, setUploadMsg] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newContact, setNewContact] = useState<ContactCreate>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  });

  const [contactToCall, setContactToCall] = useState<CallTarget | null>(null);
  const [contactToEmail, setContactToEmail] = useState<EmailLeadTarget | null>(null);
  const [contactToSms, setContactToSms] = useState<SmsLeadTarget | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  // CCT-16: per-row delete (soft-delete via DELETE /contacts/{id}), gated by
  // a confirm dialog so a stray click can't drop a contact.
  const [contactToDelete, setContactToDelete] = useState<Contact | null>(null);
  const [deleting, setDeleting] = useState(false);

  // LT-313 bulk-select state. Kept as a Set<string> of contact ids so the
  // set-membership check inside DataTable stays O(1) on large lists.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);
  const [bulkSmsOpen, setBulkSmsOpen] = useState(false);
  // LT-328: bulk lifecycle-stage edit. Manual override path — backend
  // bypasses the allowed-transitions table.
  const [bulkLifecycleOpen, setBulkLifecycleOpen] = useState(false);
  const [bulkLifecycleValue, setBulkLifecycleValue] = useState<LifecycleStage>('contacted');
  const [bulkLifecycleSaving, setBulkLifecycleSaving] = useState(false);
  // LT-534 — "Add to campaign" bulk action.
  const [addToCampaignOpen, setAddToCampaignOpen] = useState(false);
  // LT-657 — "Add to list" bulk action.
  const [addToListOpen, setAddToListOpen] = useState(false);

  const fetchContacts = useCallback(async (): Promise<Contact[]> => {
    setLoading(true);
    setErr(null);
    try {
      const rows = query.trim()
        ? await contactsAPI.list({ q: query.trim(), limit: 500 })
        : await contactsAPI.list({
            lifecycle_stage: lifecycle || undefined,
            account_id: accountId || undefined,
            since: timeRange ? sinceFor(timeRange) : undefined,
            limit: 500,
          });
      setContacts(rows);
      if (accounts.length === 0) {
        const accs = await accountsAPI.list({ limit: 200 });
        setAccounts(accs);
      }
      return rows;
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load contacts');
      return [];
    } finally {
      setLoading(false);
    }
  }, [lifecycle, accountId, timeRange, query, accounts.length]);

  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear any stale selection when the dataset changes under us (filter apply,
  // upload refresh, manual create). Otherwise the bulk bar would claim "N
  // selected" for ids that no longer live in `contacts`.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const alive = new Set<string>();
      for (const c of contacts) if (prev.has(c.id)) alive.add(c.id);
      return alive.size === prev.size ? prev : alive;
    });
  }, [contacts]);

  const applyFilters = () => fetchContacts();

  const handleUploadComplete = useCallback(
    async (result: ContactsUploadResult) => {
      const { created, listId, listName, createdContactIds } = result;
      const plural = created === 1 ? '' : 's';
      setUploadMsg(
        listId
          ? `Created ${created} contact${plural}${listName ? ` and added them to "${listName}"` : ' and added them to the list'}.`
          : `Created ${created} contact${plural}.`,
      );
      setErr(null);
      const rows = await fetchContacts();
      // When the batch wasn't assigned to a list, pre-select the freshly
      // uploaded contacts that landed in the refreshed view so the operator
      // can immediately group them (Add to list / Add to campaign).
      if (!listId && createdContactIds && createdContactIds.length > 0) {
        const fresh = new Set(createdContactIds);
        setSelectedIds(new Set(rows.filter((c) => fresh.has(c.id)).map((c) => c.id)));
      }
    },
    [fetchContacts],
  );

  const resetCreateForm = () => {
    setNewContact({ first_name: '', last_name: '', email: '', phone: '' });
  };

  const handleCreateContact = async () => {
    if (!newContact.first_name?.trim() && !newContact.last_name?.trim() && !newContact.email?.trim() && !newContact.phone?.trim()) {
      setErr('Enter at least a name, email, or phone.');
      return;
    }
    setCreating(true);
    setErr(null);
    try {
      await contactsAPI.create({
        first_name: newContact.first_name?.trim() || undefined,
        last_name: newContact.last_name?.trim() || undefined,
        email: newContact.email?.trim() || undefined,
        phone: newContact.phone?.trim() || undefined,
      });
      setShowCreate(false);
      resetCreateForm();
      await fetchContacts();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteContact = async () => {
    if (!contactToDelete) return;
    setDeleting(true);
    try {
      await contactsAPI.softDelete(contactToDelete.id);
      setToast({ type: 'success', message: `Deleted ${displayName(contactToDelete)}.` });
      setContactToDelete(null);
      await fetchContacts();
    } catch (e) {
      setToast({ type: 'error', message: e instanceof Error ? e.message : 'Delete failed.' });
    } finally {
      setDeleting(false);
    }
  };

  const accountLookup = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of accounts) map.set(a.id, a.name);
    return map;
  }, [accounts]);

  const columns = useMemo<ColumnDef<Contact>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        sortValue: (c) =>
          displayName(c).toLowerCase(),
        accessor: (c) => (
          <span className="font-medium text-slate-50">
            {[c.first_name, c.last_name].filter(Boolean).join(' ').trim() || '(no name)'}
          </span>
        ),
      },
      {
        id: 'email',
        header: 'Email',
        sortable: true,
        sortValue: (c) => (c.email ?? '').toLowerCase(),
        accessor: (c) => <span className="text-slate-300">{c.email || '—'}</span>,
      },
      {
        id: 'phone',
        header: 'Phone',
        accessor: (c) => <span className="text-slate-300">{c.phone || '—'}</span>,
      },
      {
        id: 'lifecycle',
        header: 'Lifecycle',
        sortable: true,
        sortValue: (c) => c.lifecycle_stage ?? '',
        // LT-328: render the lifecycle as a semantic <Pill> via the
        // shared <LifecyclePill> helper so the new 4 stages get their
        // own intent colors instead of the bare-text fallback.
        accessor: (c) => <LifecyclePill stage={c.lifecycle_stage} />,
      },
      {
        id: 'account',
        header: 'Account',
        accessor: (c) => (
          <span className="text-slate-300">
            {c.account_id ? accountLookup.get(c.account_id) || '(unknown)' : '—'}
          </span>
        ),
      },
      {
        id: 'last_activity',
        header: 'Last activity',
        sortable: true,
        sortValue: (c) => c.last_activity_at ? new Date(c.last_activity_at).getTime() : 0,
        accessor: (c) => (
          <span className="text-slate-400 text-xs">
            {c.last_activity_at ? new Date(c.last_activity_at).toLocaleString() : '—'}
          </span>
        ),
      },
    ],
    [accountLookup],
  );

  const renderRowActions = useCallback(
    (c: Contact) => (
      <div className="inline-flex items-center gap-1">
        <IconButton
          intent="ghost"
          size="sm"
          aria-label="Call contact"
          title={c.phone ? 'Call' : 'No phone'}
          disabled={!c.phone}
          onClick={() =>
            c.phone &&
            setContactToCall({
              id: c.id,
              name: displayName(c),
              phone: c.phone as string,
              email: c.email ?? undefined,
            })
          }
        >
          <Phone size={14} />
        </IconButton>
        <IconButton
          intent="ghost"
          size="sm"
          aria-label="Email contact"
          title={!canEmail ? 'Email unavailable on your plan' : c.email ? 'Email' : 'No email'}
          disabled={!canEmail || !c.email}
          onClick={() =>
            canEmail && c.email &&
            setContactToEmail({
              id: c.id,
              name: displayName(c),
              email: c.email as string,
            })
          }
        >
          <Mail size={14} />
        </IconButton>
        {canSms ? (
          <IconButton
            intent="ghost"
            size="sm"
            aria-label="SMS contact"
            title={c.phone ? 'SMS' : 'No phone'}
            disabled={!c.phone}
            onClick={() =>
              c.phone &&
              setContactToSms({
                id: c.id,
                name: displayName(c),
                phone: c.phone as string,
              })
            }
          >
            <MessageSquare size={14} />
          </IconButton>
        ) : null}
        <IconButton
          intent="ghost"
          size="sm"
          aria-label="Delete contact"
          title="Delete"
          onClick={() => setContactToDelete(c)}
        >
          <Trash2 size={14} />
        </IconButton>
      </div>
    ),
    [canEmail, canSms],
  );

  const selectedContacts = useMemo(
    () => contacts.filter((c) => selectedIds.has(c.id)),
    [contacts, selectedIds],
  );

  // Reset to page 1 whenever the filtered/refreshed list changes shape so the
  // user doesn't land on an out-of-range page after applying a filter.
  useEffect(() => {
    setPage(1);
  }, [contacts.length, lifecycle, accountId, timeRange, query]);

  const totalPages = Math.max(1, Math.ceil(contacts.length / PAGE_SIZE));
  const pageIndex = Math.min(page, totalPages);
  const pagedContacts = useMemo(
    () => contacts.slice((pageIndex - 1) * PAGE_SIZE, pageIndex * PAGE_SIZE),
    [contacts, pageIndex],
  );

  const bulkEmailLeads = useMemo(
    () =>
      selectedContacts.map((c) => ({
        id: c.id,
        name: displayName(c),
        email: c.email ?? '',
      })),
    [selectedContacts],
  );

  const bulkSmsLeads = useMemo(
    () =>
      selectedContacts.map((c) => ({
        id: c.id,
        name: displayName(c),
        phone: c.phone ?? '',
      })),
    [selectedContacts],
  );

  const renderBulkActions = useCallback(
    () => (
      <div className="flex items-center gap-2">
        {canEmail ? (
          <Button
            intent="secondary"
            size="sm"
            onClick={() => setBulkEmailOpen(true)}
            disabled={selectedContacts.length === 0}
          >
            <Mail className="w-3.5 h-3.5" />
            <span>Bulk email</span>
          </Button>
        ) : null}
        {canSms ? (
          <Button
            intent="secondary"
            size="sm"
            onClick={() => setBulkSmsOpen(true)}
            disabled={selectedContacts.length === 0}
          >
            <MessageSquare className="w-3.5 h-3.5" />
            <span>Bulk SMS</span>
          </Button>
        ) : null}
        <Button
          intent="secondary"
          size="sm"
          onClick={() => setBulkLifecycleOpen(true)}
          disabled={selectedContacts.length === 0}
        >
          <Users className="w-3.5 h-3.5" />
          <span>Set lifecycle</span>
        </Button>
        <Button
          intent="secondary"
          size="sm"
          onClick={() => setAddToListOpen(true)}
          disabled={selectedContacts.length === 0}
        >
          <ListPlus className="w-3.5 h-3.5" />
          <span>Add to list</span>
        </Button>
        <Button
          intent="secondary"
          size="sm"
          onClick={() => setAddToCampaignOpen(true)}
          disabled={selectedContacts.length === 0}
        >
          <Megaphone className="w-3.5 h-3.5" />
          <span>Add to campaign</span>
        </Button>
      </div>
    ),
    [canEmail, canSms, selectedContacts.length],
  );

  const handleBulkLifecycleApply = useCallback(async () => {
    if (selectedContacts.length === 0) return;
    setBulkLifecycleSaving(true);
    try {
      const result = await contactsAPI.bulkEditLifecycle(
        selectedContacts.map((c) => c.id),
        bulkLifecycleValue,
      );
      setBulkLifecycleOpen(false);
      setSelectedIds(new Set());
      setToast({
        type: 'success',
        message: `Updated ${result.updated} contact${result.updated === 1 ? '' : 's'} to ${LIFECYCLE_STAGE_LABELS[bulkLifecycleValue]}.`,
      });
      await fetchContacts();
    } catch (e) {
      setToast({
        type: 'error',
        message: e instanceof Error ? e.message : 'Bulk lifecycle update failed.',
      });
    } finally {
      setBulkLifecycleSaving(false);
    }
  }, [selectedContacts, bulkLifecycleValue, fetchContacts]);

  return (
    <PageShell className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-ds-lg bg-accent flex items-center justify-center shadow-ds-glow-accent">
            <Users className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-xl font-semibold text-slate-50">Contacts</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button intent="secondary" onClick={() => setShowUploadModal(true)}>
            <Upload className="w-4 h-4" />
            <span>Upload CSV</span>
          </Button>
          <Button intent="primary" onClick={() => setShowCreate(true)}>
            <Plus className="w-4 h-4" />
            <span>Add contact</span>
          </Button>
        </div>
      </div>

      {uploadMsg && (
        <div className="mb-3 text-sm text-ok-text border border-ok-border rounded-ds-lg px-3 py-2 bg-ok-tint">
          {uploadMsg}
        </div>
      )}
      {err && (
        <div className="mb-3 text-sm text-danger-text border border-danger-border rounded-ds-lg px-3 py-2 bg-danger-tint">
          {err}
        </div>
      )}

      <div className="bg-surface-1 border border-white/[0.08] rounded-ds-xl p-3 mb-3 flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] font-medium tracking-wider uppercase text-slate-400">Search</label>
          <div className="flex items-center border border-white/[0.08] bg-surface-2 rounded-ds-lg px-2 py-1 focus-within:border-accent transition-colors">
            <Search size={14} className="text-slate-400" />
            <input
              type="text"
              placeholder="Name, email, or phone"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
              className="outline-none px-2 py-1 text-sm w-64 bg-transparent text-slate-50 placeholder:text-slate-500"
            />
          </div>
        </div>
        <Select
          label="Lifecycle"
          value={lifecycle}
          onChange={(e) => setLifecycle(e.target.value as '' | LifecycleStage)}
        >
          {LIFECYCLE_FILTERS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </Select>
        <Select
          label="Account"
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
        >
          <option value="">All accounts</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </Select>
        <Select
          label="Created"
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value as '' | 'week' | 'month' | 'quarter')}
        >
          {TIME_RANGES.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </Select>
        <Button intent="primary" onClick={applyFilters}>Apply</Button>
      </div>

      {loading ? (
        <PageLoader />
      ) : contacts.length === 0 && !query && !lifecycle && !accountId && !timeRange ? (
        // LT-314: true first-run empty state (no contacts AND no active
        // filters). Filtered-empty still falls through to the DataTable's
        // own emptyState, which surfaces "No contacts match these
        // filters." inside the table chrome.
        <EmptyState
          surface="contacts_list"
          icon={Users}
          headline="Import your first contacts"
          subhead="Upload a CSV of leads or add contacts one by one. Columns in the CSV are auto-mapped; invalid rows stay editable until you fix them."
          primary={{
            label: 'Upload CSV',
            icon: Upload,
            onClick: () => setShowUploadModal(true),
          }}
          secondary={{
            label: 'Add contact',
            icon: Plus,
            onClick: () => setShowCreate(true),
          }}
        />
      ) : (
        <>
          <DataTable<Contact>
            ariaLabel="Contacts"
            data={pagedContacts}
            columns={columns}
            getRowKey={(c) => c.id}
            onRowClick={(c) => navigate(`/contacts/${c.id}`)}
            enableSelection
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            renderRowActions={renderRowActions}
            renderBulkActions={renderBulkActions}
            emptyState={
              <div className="text-slate-400 py-2">
                No contacts match these filters.
              </div>
            }
          />
          {contacts.length > PAGE_SIZE ? (
            <div className="flex items-center justify-between mt-3 text-xs text-slate-400">
              <div>
                Showing {(pageIndex - 1) * PAGE_SIZE + 1}
                –{Math.min(pageIndex * PAGE_SIZE, contacts.length)}
                {' '}of {contacts.length}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  intent="ghost"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={pageIndex === 1}
                >
                  Prev
                </Button>
                <span className="text-slate-300">
                  Page {pageIndex} of {totalPages}
                </span>
                <Button
                  intent="ghost"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={pageIndex === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          ) : null}
        </>
      )}

      {showCreate && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => !creating && setShowCreate(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="contact-create-title"
        >
          <div
            className="w-full max-w-md bg-surface-1 border border-white/[0.08] rounded-ds-xl p-5 shadow-ds-e3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 id="contact-create-title" className="text-lg font-semibold text-slate-50">Add contact</h2>
              <IconButton
                intent="ghost"
                size="sm"
                aria-label="Close"
                onClick={() => !creating && setShowCreate(false)}
                disabled={creating}
              >
                <X size={16} />
              </IconButton>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="First name"
                  value={newContact.first_name ?? ''}
                  onChange={(e) => setNewContact({ ...newContact, first_name: e.target.value })}
                />
                <Input
                  label="Last name"
                  value={newContact.last_name ?? ''}
                  onChange={(e) => setNewContact({ ...newContact, last_name: e.target.value })}
                />
              </div>
              <Input
                label="Email"
                type="email"
                value={newContact.email ?? ''}
                onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                placeholder="name@example.com"
              />
              <Input
                label="Phone"
                type="tel"
                value={newContact.phone ?? ''}
                onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                placeholder="+61 4xx xxx xxx"
              />
            </div>
            <div className="flex items-center justify-end gap-2 mt-5">
              <Button intent="ghost" onClick={() => !creating && setShowCreate(false)} disabled={creating}>
                Cancel
              </Button>
              <Button intent="primary" onClick={handleCreateContact} disabled={creating}>
                {creating ? 'Creating…' : 'Create'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <CallLeadModal
        isOpen={contactToCall !== null}
        onClose={() => setContactToCall(null)}
        lead={contactToCall}
        setToast={setToast}
      />
      <SendEmailModal
        isOpen={contactToEmail !== null}
        onClose={() => setContactToEmail(null)}
        lead={contactToEmail}
        setToast={setToast}
      />
      <SendSMSModal
        isOpen={contactToSms !== null}
        onClose={() => setContactToSms(null)}
        lead={contactToSms}
        setToast={setToast}
      />
      <BulkEmailModal
        isOpen={bulkEmailOpen}
        onClose={() => setBulkEmailOpen(false)}
        leads={bulkEmailLeads}
        setToast={setToast}
      />
      <BulkSMSModal
        isOpen={bulkSmsOpen}
        onClose={() => setBulkSmsOpen(false)}
        leads={bulkSmsLeads}
        setToast={setToast}
      />
      <AddToCampaignModal
        isOpen={addToCampaignOpen}
        onClose={() => setAddToCampaignOpen(false)}
        contactIds={selectedContacts.map((c) => c.id)}
        setToast={setToast}
        onAdded={() => setSelectedIds(new Set())}
      />
      <AddToListModal
        isOpen={addToListOpen}
        onClose={() => setAddToListOpen(false)}
        contactIds={selectedContacts.map((c) => c.id)}
        setToast={setToast}
        onAdded={() => setSelectedIds(new Set())}
      />
      {bulkLifecycleOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => !bulkLifecycleSaving && setBulkLifecycleOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="bulk-lifecycle-title"
        >
          <div
            className="w-full max-w-md bg-surface-1 border border-white/[0.08] rounded-ds-xl p-5 shadow-ds-e3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 id="bulk-lifecycle-title" className="text-lg font-semibold text-slate-50">
                Set lifecycle stage
              </h2>
              <IconButton
                intent="ghost"
                size="sm"
                aria-label="Close"
                onClick={() => !bulkLifecycleSaving && setBulkLifecycleOpen(false)}
                disabled={bulkLifecycleSaving}
              >
                <X size={16} />
              </IconButton>
            </div>
            <div className="space-y-4">
              <p className="text-sm text-slate-300">
                Set the lifecycle stage for{' '}
                <span className="font-semibold text-slate-50">
                  {selectedContacts.length}
                </span>{' '}
                contact{selectedContacts.length === 1 ? '' : 's'}.
                Manual override — automated transitions are bypassed.
              </p>
              <Select
                label="New lifecycle stage"
                value={bulkLifecycleValue}
                onChange={(e) => setBulkLifecycleValue(e.target.value as LifecycleStage)}
                disabled={bulkLifecycleSaving}
              >
                {LIFECYCLE_STAGES.map((s) => (
                  <option key={s} value={s}>
                    {LIFECYCLE_STAGE_LABELS[s]}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex items-center justify-end gap-2 mt-5">
              <Button
                intent="ghost"
                onClick={() => !bulkLifecycleSaving && setBulkLifecycleOpen(false)}
                disabled={bulkLifecycleSaving}
              >
                Cancel
              </Button>
              <Button
                intent="primary"
                onClick={handleBulkLifecycleApply}
                disabled={bulkLifecycleSaving || selectedContacts.length === 0}
              >
                {bulkLifecycleSaving ? 'Updating…' : 'Apply'}
              </Button>
            </div>
          </div>
        </div>
      )}
      {contactToDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => !deleting && setContactToDelete(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="contact-delete-title"
        >
          <div
            className="w-full max-w-md bg-surface-1 border border-white/[0.08] rounded-ds-xl p-5 shadow-ds-e3"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="contact-delete-title" className="text-lg font-semibold text-slate-50 mb-2">
              Delete contact
            </h2>
            <p className="text-sm text-slate-300">
              Delete{' '}
              <span className="font-semibold text-slate-50">{displayName(contactToDelete)}</span>?
              They'll be removed from your contacts list. Past call and activity
              history is kept.
            </p>
            <div className="flex items-center justify-end gap-2 mt-5">
              <Button
                intent="ghost"
                onClick={() => !deleting && setContactToDelete(null)}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button intent="danger" onClick={handleDeleteContact} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
      {toast ? (
        <div
          className={`fixed bottom-6 right-6 z-50 px-4 py-2 rounded-ds-lg text-sm shadow-ds-e3 border ${
            toast.type === 'success'
              ? 'bg-ok-tint border-ok-border text-ok-text'
              : 'bg-danger-tint border-danger-border text-danger-text'
          }`}
          role="status"
        >
          {toast.message}
          <button
            type="button"
            onClick={() => setToast(null)}
            className="ml-3 text-xs text-slate-400 hover:text-slate-50"
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      ) : null}

      <ContactsUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploaded={handleUploadComplete}
      />
    </PageShell>
  );
};

export default ContactsPage;
